import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { CaretLeft, CaretRight, Plus, Trash, CalendarBlank, UploadSimple, X, PencilSimple, User, House } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { fdate } from "@/lib/format";
import { fileUrl } from "@/components/ImageUpload";
import { PageHeader } from "@/components/common";
import { TextField, SelectField, AreaField } from "@/components/fields";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const DOW = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const TIPOS = [["visita", "Visita"], ["reunion", "Reunión"], ["llamada", "Llamada"], ["cita", "Cita"], ["vencimiento", "Vencimiento"], ["inspeccion", "Inspección"], ["mantenimiento", "Mantenimiento"], ["renovacion", "Renovación"], ["cobranza", "Cobranza"], ["juridico", "Jurídico"], ["otro", "Otro"]].map(([value, label]) => ({ value, label }));
const TIPO_CLS = { visita: "bg-emerald-500", reunion: "bg-indigo-500", llamada: "bg-sky-500", cita: "bg-amber-500", vencimiento: "bg-red-500", inspeccion: "bg-orange-500", mantenimiento: "bg-orange-600", renovacion: "bg-purple-500", cobranza: "bg-teal-600", juridico: "bg-rose-600", otro: "bg-neutral-500" };
const LEGEND = [["visita", "Visita"], ["reunion", "Reunión"], ["llamada", "Llamada"], ["vencimiento", "Vencimiento"], ["cita", "Cita"], ["otro", "Otro"]];
const STATUS = {
  pendiente: { label: "Pendiente", dot: "bg-neutral-400", badge: "bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300" },
  terminado: { label: "Terminado", dot: "bg-emerald-500", badge: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300" },
  reagendar: { label: "Reagendar", dot: "bg-amber-500", badge: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300" },
  cancelado: { label: "Cancelado", dot: "bg-red-500", badge: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400" },
  observacion: { label: "Observación", dot: "bg-sky-500", badge: "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300" },
};
const STATUS_OPTS = Object.entries(STATUS).map(([value, m]) => ({ value, label: m.label }));
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const EMPTY_EVENT = { titulo: "", tipo: "cita", fecha: iso(new Date()), hora: "", hora_fin: "", cliente: "", inmueble_id: "", estatus: "pendiente", descripcion: "", imagenes: [] };

function StatusBadge({ estatus }) {
  const m = STATUS[estatus] || STATUS.pendiente;
  return <span data-testid={`event-status-${estatus}`} className={`inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10px] font-semibold ${m.badge}`}><span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />{m.label}</span>;
}

function ImagesField({ value = [], onChange }) {
  const [loading, setLoading] = useState(false);
  const ref = useRef();
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Selecciona una imagen"); return; }
    setLoading(true);
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post("/upload", fd); onChange([...value, data.path]); toast.success("Imagen subida"); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setLoading(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div className="sm:col-span-2">
      <label className="text-xs font-medium">Imágenes</label>
      <input ref={ref} type="file" accept="image/*" className="hidden" data-testid="event-imagen" onChange={handle} />
      <div className="mt-1 flex flex-wrap gap-2">
        {value.map((p) => (
          <div key={p} className="relative w-16 h-16 rounded-sm overflow-hidden border border-border group">
            <img src={fileUrl(p)} alt="adjunto" className="w-full h-full object-cover" />
            <button type="button" onClick={() => onChange(value.filter((x) => x !== p))}
              className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100"><X size={12} /></button>
          </div>
        ))}
        <button type="button" data-testid="add-event-image" onClick={() => ref.current?.click()} disabled={loading}
          className="w-16 h-16 rounded-sm border border-dashed border-border flex flex-col items-center justify-center gap-1 text-muted-foreground hover:border-primary hover:text-primary transition-colors">
          {loading ? <span className="text-[10px]">Subiendo…</span> : (<><UploadSimple size={16} /><span className="text-[10px]">Añadir</span></>)}
        </button>
      </div>
    </div>
  );
}

export default function Agenda() {
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [events, setEvents] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selected, setSelected] = useState(iso(new Date()));
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_EVENT);

  const first = new Date(cursor.y, cursor.m, 1);
  const startOffset = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const desde = iso(new Date(cursor.y, cursor.m, 1));
  const hasta = iso(new Date(cursor.y, cursor.m, daysInMonth));

  const load = useCallback(() => { api.get("/agenda/events", { params: { desde, hasta } }).then((r) => setEvents(r.data)); }, [desde, hasta]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/properties").then((r) => setProperties(r.data)).catch(() => {}); }, []);

  const propName = (id) => properties.find((p) => p.id === id)?.nombre || "";
  const setF = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const move = (delta) => setCursor((c) => { const d = new Date(c.y, c.m + delta, 1); return { y: d.getFullYear(), m: d.getMonth() }; });
  const openAdd = (dateStr) => { setForm({ ...EMPTY_EVENT, fecha: dateStr }); setEditId(null); setOpen(true); };
  const openEdit = (ev) => { setForm({ ...EMPTY_EVENT, ...ev }); setEditId(ev.id); setOpen(true); };

  const save = async () => {
    if (!form.titulo) return toast.error("Título obligatorio");
    try {
      if (editId) { await api.put(`/agenda/events/${editId}`, form); toast.success("Evento actualizado"); }
      else { await api.post("/agenda/events", form); toast.success("Evento agregado"); }
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id, e) => { if (e) e.stopPropagation(); if (!window.confirm("¿Eliminar evento?")) return; await api.delete(`/agenda/events/${id}`); load(); };

  const evOf = (dateStr) => events.filter((e) => e.fecha === dateStr).sort((a, b) => (a.hora || "99:99").localeCompare(b.hora || "99:99"));
  const selEvents = evOf(selected);
  const proximos = [...events].filter((e) => e.fecha >= iso(new Date()) && e.estatus !== "cancelado").sort((a, b) => (a.fecha + (a.hora || "99:99")).localeCompare(b.fecha + (b.hora || "99:99"))).slice(0, 6);

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const todayStr = iso(new Date());

  return (
    <div data-testid="agenda-page">
      <PageHeader title="Agenda" subtitle="Citas ordenadas por horario, con estatus y reagendado"
        actions={<Button data-testid="new-event-btn" className="rounded-sm gap-2" onClick={() => openAdd(selected)}><Plus size={16} /> Nuevo evento</Button>} />

      <div className="flex flex-wrap gap-2 mb-5">
        {LEGEND.map(([t, l]) => (
          <span key={t} className="inline-flex items-center gap-1.5 text-xs font-medium border border-border rounded-full px-2.5 py-1">
            <span className={`w-2 h-2 rounded-full ${TIPO_CLS[t]}`} /> {l}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-card border border-border rounded-sm p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-lg" data-testid="agenda-month">{MESES[cursor.m]} de {cursor.y}</h3>
            <div className="flex gap-1">
              <Button variant="outline" size="icon" className="rounded-sm" data-testid="agenda-prev" onClick={() => move(-1)}><CaretLeft size={16} /></Button>
              <Button variant="outline" size="sm" className="rounded-sm" onClick={() => { const d = new Date(); setCursor({ y: d.getFullYear(), m: d.getMonth() }); setSelected(iso(d)); }}>Hoy</Button>
              <Button variant="outline" size="icon" className="rounded-sm" data-testid="agenda-next" onClick={() => move(1)}><CaretRight size={16} /></Button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1 mb-1">{DOW.map((d) => <div key={d} className="text-xs font-semibold text-muted-foreground text-center py-1">{d}</div>)}</div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((day, i) => {
              if (!day) return <div key={i} className="min-h-20 rounded-sm" />;
              const dateStr = iso(new Date(cursor.y, cursor.m, day));
              const evs = evOf(dateStr);
              const isSel = dateStr === selected;
              return (
                <div key={i} data-testid={`day-${dateStr}`} onClick={() => setSelected(dateStr)} className={`min-h-20 rounded-sm border p-1.5 cursor-pointer hover:border-primary transition-colors ${isSel ? "border-primary ring-1 ring-primary bg-primary/5" : dateStr === todayStr ? "border-primary/40" : "border-border"}`}>
                  <div className="text-xs font-semibold mb-1">{day}</div>
                  <div className="space-y-0.5">
                    {evs.slice(0, 2).map((e) => (
                      <div key={e.id} className={`flex items-center gap-1 text-[10px] truncate ${e.estatus === "cancelado" ? "line-through opacity-60" : ""}`}><span className={`w-1.5 h-1.5 rounded-full shrink-0 ${TIPO_CLS[e.tipo]}`} /><span className="truncate">{e.hora ? `${e.hora} ` : ""}{e.titulo}</span></div>
                    ))}
                    {evs.length > 2 && <div className="text-[10px] text-muted-foreground">+{evs.length - 2}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-card border border-border rounded-sm p-4">
          <div className="flex items-center justify-between mb-1">
            <div className="font-display font-bold" data-testid="agenda-selected">{fdate(selected)}</div>
            <Button variant="ghost" size="icon" className="rounded-sm" data-testid="add-event-day" onClick={() => openAdd(selected)}><Plus size={18} /></Button>
          </div>
          <div className="text-xs text-muted-foreground mb-3">{selEvents.length} evento(s) · orden cronológico</div>
          {selEvents.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground"><CalendarBlank size={28} className="mx-auto mb-2 opacity-60" /><p className="text-sm">No hay eventos este día</p></div>
          ) : (
            <ul className="space-y-2">
              {selEvents.map((e) => (
                <li key={e.id} data-testid={`event-item-${e.id}`} className="group flex items-start gap-2 border border-border rounded-sm p-2 cursor-pointer hover:border-primary transition-colors" onClick={() => openEdit(e)}>
                  <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${TIPO_CLS[e.tipo]}`} />
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium truncate ${e.estatus === "cancelado" ? "line-through opacity-70" : ""}`}>{(e.hora ? `${e.hora}${e.hora_fin ? `–${e.hora_fin}` : ""} · ` : "")}{e.titulo}</div>
                    <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                      <StatusBadge estatus={e.estatus || "pendiente"} />
                      {e.cliente && <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground truncate"><User size={11} /> {e.cliente}</span>}
                      {e.inmueble_id && <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground truncate"><House size={11} /> {propName(e.inmueble_id)}</span>}
                    </div>
                    {e.descripcion && <div className="text-xs text-muted-foreground truncate mt-0.5">{e.descripcion}</div>}
                    {e.imagenes?.length > 0 && (
                      <div className="flex gap-1 mt-1.5 flex-wrap">
                        {e.imagenes.map((p) => (
                          <a key={p} href={fileUrl(p)} target="_blank" rel="noreferrer" onClick={(ev) => ev.stopPropagation()}>
                            <img src={fileUrl(p)} alt="adjunto" className="w-10 h-10 rounded-sm object-cover border border-border" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100">
                    <button data-testid={`edit-event-${e.id}`} onClick={(ev) => { ev.stopPropagation(); openEdit(e); }} className="text-muted-foreground hover:text-primary"><PencilSimple size={15} /></button>
                    <button data-testid={`del-event-${e.id}`} onClick={(ev) => del(e.id, ev)} className="text-red-600"><Trash size={14} /></button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="bg-card border border-border rounded-sm p-4 mt-4">
        <h3 className="font-display font-bold mb-3">Próximos eventos</h3>
        {proximos.length === 0 ? <p className="text-center text-muted-foreground py-6 text-sm">No hay eventos próximos</p> : (
          <ul className="divide-y divide-border">
            {proximos.map((e) => (
              <li key={e.id} className="flex items-center gap-3 py-2 text-sm cursor-pointer hover:bg-muted/40 rounded-sm px-1" onClick={() => openEdit(e)}>
                <span className={`w-2 h-2 rounded-full shrink-0 ${TIPO_CLS[e.tipo]}`} />
                <span className="w-28 text-xs text-muted-foreground tabular-nums">{fdate(e.fecha)}{e.hora ? ` ${e.hora}` : ""}</span>
                <span className="flex-1 truncate font-medium">{e.titulo}</span>
                <StatusBadge estatus={e.estatus || "pendiente"} />
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Editar evento" : "Nuevo evento"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <TextField label="Título" value={form.titulo} onChange={setF("titulo")} testId="event-titulo" col={2} />
            <SelectField label="Motivo / Tipo" value={form.tipo} onChange={setF("tipo")} testId="event-tipo" options={TIPOS} />
            <SelectField label="Estatus" value={form.estatus} onChange={setF("estatus")} testId="event-estatus" options={STATUS_OPTS} />
            <TextField label="Fecha" type="date" value={form.fecha} onChange={setF("fecha")} testId="event-fecha" />
            <TextField label="Hora inicio" type="time" value={form.hora} onChange={setF("hora")} testId="event-hora" />
            <TextField label="Hora fin (duración)" type="time" value={form.hora_fin} onChange={setF("hora_fin")} testId="event-hora-fin" />
            <TextField label="Cliente" value={form.cliente} onChange={setF("cliente")} testId="event-cliente" />
            <SelectField label="Inmueble" value={form.inmueble_id} onChange={setF("inmueble_id")} testId="event-inmueble" col={2}
              options={properties.map((p) => ({ value: p.id, label: p.nombre }))} placeholder="Sin inmueble" />
            <AreaField label="Observaciones" value={form.descripcion} onChange={setF("descripcion")} testId="event-observaciones" />
            <ImagesField value={form.imagenes} onChange={(imgs) => setForm((f) => ({ ...f, imagenes: imgs }))} />
          </div>
          {form.estatus === "reagendar" && (
            <p className="text-xs text-amber-600 dark:text-amber-400 -mt-1" data-testid="reagendar-hint">
              Cita marcada para reagendar: ajusta la fecha y hora arriba y guarda; no necesitas recrear el evento.
            </p>
          )}
          <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancelar</Button><Button className="rounded-sm" data-testid="save-event-btn" onClick={save}>Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
